import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Key, ArrowLeft } from 'lucide-react'

export function TermsOfService() {
  return (
    <>
      <Helmet>
        <title>Terms of Service | Twenty</title>
        <meta name="description" content="Terms of service for using Twenty infrastructure platform." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border">
          <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              <span className="font-semibold">Twenty</span>
            </Link>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-3xl font-semibold mb-8">Terms of Service</h1>
          
          <div className="prose prose-neutral max-w-none">
            <p className="text-muted-foreground mb-6">Last updated: {new Date().toLocaleDateString()}</p>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground">
                By using Twenty, you agree to these terms. If you don't agree, don't use the service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">2. Your Account</h2>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>You must provide accurate information</li>
                <li>You are responsible for your API keys</li>
                <li>You are responsible for all activity under your account</li>
                <li>Notify us immediately of unauthorized use</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">3. Acceptable Use</h2>
              <p className="text-muted-foreground mb-4">You may not use the service to:</p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Violate any laws</li>
                <li>Send spam or unsolicited communications</li>
                <li>Distribute malware or viruses</li>
                <li>Attack or interfere with other systems</li>
                <li>Mine cryptocurrency</li>
                <li>Store or process illegal content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">4. Fair Use</h2>
              <p className="text-muted-foreground">
                We monitor for abuse. If your usage is excessive or impacts other users, 
                we may contact you to discuss a custom plan. We reserve the right to 
                suspend accounts for abuse.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">5. Generative AI and Third-Party Services</h2>
              <p className="text-muted-foreground mb-4">
                Twenty provides access to generative AI tools and other third-party services. 
                We aim to support you by following the Terms of Service and Acceptable Use Policies 
                of our underlying providers (including but not limited to AI model providers, 
                infrastructure providers, and API services).
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>You remain responsible for ensuring your use of these services complies 
                with all applicable provider Terms of Service.</strong> This includes but is not 
                limited to:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Not generating illegal, harmful, or prohibited content</li>
                <li>Not using AI services to infringe intellectual property rights</li>
                <li>Not attempting to bypass safety filters or misuse AI systems</li>
                <li>Complying with content policies and usage restrictions set by providers</li>
                <li>Using generated content in accordance with provider licensing terms</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                You must remain compliant with provider Terms of Service to the best of your 
                ability. Twenty does not monitor the content of your API calls or generated outputs, 
                but we reserve the right to investigate reports of abuse. <strong>Failure to comply 
                with provider Terms of Service may result in your account being suspended or 
                permanently blocked</strong>, and you may be liable for damages or fees imposed on 
                Twenty by our providers due to your violations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">6. Payment and Refunds</h2>
              <p className="text-muted-foreground mb-4">
                All purchases are final and non-refundable. By subscribing to Twenty, you are 
                purchasing pre-paid growth capacity and API access that is reserved exclusively 
                for your account. Once capacity is allocated, it cannot be resold or reassigned.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>Consumed services are strictly non-refundable.</strong> This includes:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>API calls made using your keys</li>
                <li>Data storage used (secrets, files, or other assets)</li>
                <li>Compute resources consumed</li>
                <li>Any usage of infrastructure capacity</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                <strong>24-Hour Grace Period:</strong> You may request a refund within 24 hours of 
                purchase if your account has not been used (no API calls, no keys created, no data 
                stored). Refunds are granted at our sole discretion and only if the account remains 
                completely unused. Contact billing@twenty.sh immediately if you believe you 
                qualify.
              </p>
              <p className="text-muted-foreground mt-4">
                You may cancel your subscription at any time. Cancellation takes effect at the 
                end of your current billing period. No refunds will be issued for the current 
                period or any previous periods, regardless of usage levels.
              </p>
              <p className="text-muted-foreground mt-4">
                <strong>Chargebacks:</strong> Initiating a chargeback or payment dispute with your 
                bank or payment provider will result in <strong>immediate account closure</strong> 
                and permanent ban from the service. All data will be deleted. We reserve the right 
                to pursue legal action to recover fees and costs associated with fraudulent 
                chargebacks.
              </p>
              <p className="text-muted-foreground mt-4">
                Twenty aims to be fully compliant with all applicable laws and regulations. We 
                will work to comply with legal requirements where possible, including consumer 
                protection laws that may apply in your jurisdiction. Nothing in these terms is 
                intended to override your statutory rights where such rights cannot be excluded 
                by contract.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">7. Service Availability</h2>
              <p className="text-muted-foreground">
                We aim for high availability but provide no guarantees. The service is 
                provided "as is" without warranties of any kind.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">8. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                We are not liable for any damages arising from your use of the service. 
                Our liability is limited to the amount you paid us in the past 12 months.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">9. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may update these terms. Continued use after changes constitutes 
                acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium mb-4">10. Contact</h2>
              <p className="text-muted-foreground">
                Questions? Email us at legal@twenty.sh
              </p>
            </section>
          </div>
        </main>

        <footer className="border-t border-border py-8">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Twenty
            </p>
          </div>
        </footer>
      </div>
    </>
  )
}
